import { createFileRoute, notFound, Outlet } from '@tanstack/react-router'
import { getChapterBySlug, useChapterText } from '@/lib/chapters'
import { usePageSeo } from '@/lib/page-seo'
import { resolveSite, siteBrand } from '@/lib/site'
import { useLocale } from '@/lib/locale'
import { useUi } from '@/lib/i18n/ui'
import { QATAR_BRAND } from '@/components/qatar/brand'

/**
 * Layout-only parent route for /basics/$basicsSlug + /basics/$basicsSlug/start.
 * Validates the slug, then yields to the matching child:
 *   - basics.$basicsSlug.index.tsx → chapter overview
 *   - basics.$basicsSlug.start.tsx → slide player
 */
export const Route = createFileRoute('/basics/$basicsSlug')({
  beforeLoad: ({ params }) => {
    if (!getChapterBySlug(params.basicsSlug)) throw notFound()
  },
  component: BasicsChapterLayout,
})

// useChapterText is a hook, so it needs a stable entry even on the (never
// rendered) not-found path; beforeLoad already threw for unknown slugs.
const CHAPTER_FALLBACK = {
  number: 0,
  basicsSlug: '',
  chapter: { id: '', title: '', description: '', estimatedMinutes: 0, slides: [] },
  summary: '',
  takeaways: [],
  ar: { title: '', description: '', summary: '', takeaways: [] },
}

function BasicsChapterLayout() {
  const { basicsSlug } = Route.useParams()
  const entry = getChapterBySlug(basicsSlug)
  const locale = useLocale()
  const t = useUi()
  const text = useChapterText(entry ?? CHAPTER_FALLBACK)
  const isQatar = resolveSite() === 'qatar'
  usePageSeo(
    entry
      ? {
          title: isQatar
            ? `${text.title} · ${t.basicsEyebrow} · ${QATAR_BRAND.program[locale]}`
            : `${entry.chapter.title} · Scam Defense Fundamentals · ${siteBrand().name}`,
          description: isQatar ? text.summary : entry.summary,
          // The /start slide player canonicalizes to the chapter overview:
          // same chapter, and the player itself has no indexable content.
          canonicalPath: `/basics/${basicsSlug}`,
        }
      : null,
  )
  return <Outlet />
}
